import { AsyncPipe, CurrencyPipe, NgClass, NgFor, NgIf, NgTemplateOutlet } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  QueryList,
  Renderer2,
  ViewChildren,
} from '@angular/core';
import {
  AbstractControl,
  FormsModule,
  ReactiveFormsModule,
  UntypedFormControl,
  UntypedFormGroup,
  ValidationErrors,
  Validators,
} from '@angular/forms';
import { MatCheckbox } from '@angular/material/checkbox';
import { MatError } from '@angular/material/input';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { ALLOCATION_STATUS_ICON_DETAILS, AbstractArTran, AllocationStatus, CreateArTranPayload } from '@anduin/api';
import {
  CheckboxState,
  DEFAULT_LOADABLE_STATE,
  Loadable,
  LoadableHelper,
  LoadableStatus,
  canShowValidationErrors,
  distinctUntilObjectChanged,
  emitValueIfValid,
  extractFormattedNumber,
  fromSync,
  getControlValueChanges,
  getFirstOfQueryList,
  getLoadedResults,
  getSum,
  hashObject,
  ignoreEmpty,
  ignoreFalsy,
  ignoreNullish,
  isEmpty,
  removeDuplicates,
  roundMonetaryValue,
} from '@anduin/shared';
import { partition, xor } from 'lodash';
import { BehaviorSubject, Observable, Subject, combineLatest, defer, of } from 'rxjs';
import {
  delay,
  distinctUntilChanged,
  distinctUntilKeyChanged,
  filter,
  map,
  pairwise,
  pluck,
  shareReplay,
  skip,
  startWith,
  switchMap,
  take,
  takeUntil,
  tap,
} from 'rxjs/operators';
import { IconComponent } from '../../../../shared/components/icon/icon.component';
import { NumberOnlyDirective } from '../../../../shared/directives/number-only.directive';
import { ExtendedCurrencyPipe } from '../../../../shared/pipes/extended-currency.pipe';

export type BasicAllocations = Record<string, number>;
export type BasicAllocationStatuses = Record<string, AllocationStatus>;

enum NavigationKey {
  ARROW_UP = 'ArrowUp',
  ARROW_DOWN = 'ArrowDown',
  ARROW_LEFT = 'ArrowLeft',
  ARROW_RIGHT = 'ArrowRight',
  HOME = 'Home',
  END = 'End',
  ESCAPE = 'Escape',
}

const navigationKeys = Object.values(NavigationKey);

enum SelectionKey {
  ENTER = 'Enter',
  SPACE = ' ',
}

const selectionKeys = Object.values(SelectionKey);

const numberKeys = Array.from({ length: 10 }, (_, i) => `${i}`);

@Component({
  selector: 'app-transaction-allocations',
  templateUrl: './transaction-allocations.component.html',
  styleUrls: ['./transaction-allocations.component.scss'],
  imports: [
    NgIf,
    MatCheckbox,
    IconComponent,
    NgClass,
    NgFor,
    NgTemplateOutlet,
    MatProgressSpinner,
    FormsModule,
    NumberOnlyDirective,
    ReactiveFormsModule,
    MatError,
    AsyncPipe,
    CurrencyPipe,
    ExtendedCurrencyPipe,
  ],
})
export class TransactionAllocationsComponent implements AfterViewInit, OnDestroy {
  //#region Inputs/Outputs

  isEditActive$ = new BehaviorSubject<boolean>(undefined);
  @Input() set isEditActive(isEditActive: boolean) {
    emitValueIfValid(isEditActive, this.isEditActive$);
  }

  selectedArTran$ = new BehaviorSubject<Loadable<AbstractArTran>>(DEFAULT_LOADABLE_STATE);
  @Input() set selectedArTran(selectedArTran: Loadable<AbstractArTran>) {
    this.isInCreateFlow = false;
    emitValueIfValid(selectedArTran, this.selectedArTran$);
  }

  @Input() set newArTran(newArTran: CreateArTranPayload) {
    this.isInCreateFlow = true;
    if (newArTran == undefined) {
      return;
    }

    const marshaledNewArTran = AbstractArTran.marshalFromCreatePayload(newArTran);
    const spoofedArTranClass = AbstractArTran.create(marshaledNewArTran);
    this.selectedArTran$.next({ status: LoadableStatus.LOADED, data: spoofedArTranClass });
  }

  linkedArTrans$ = new BehaviorSubject<Loadable<AbstractArTran[]>>(DEFAULT_LOADABLE_STATE);
  @Input() set linkedArTrans(linkedArTrans: Loadable<AbstractArTran[]>) {
    // Sort by remaining amount (largest to smallest) if data is present
    if (linkedArTrans?.data) {
      const sortedLinkedArTrans = {
        ...linkedArTrans,
        data: [...linkedArTrans.data].sort((a, b) => b.amountRemaining - a.amountRemaining),
      };
      emitValueIfValid(sortedLinkedArTrans, this.linkedArTrans$);
    } else {
      emitValueIfValid(linkedArTrans, this.linkedArTrans$);
    }
  }

  linkableArTrans$ = new BehaviorSubject<Loadable<AbstractArTran[]>>(DEFAULT_LOADABLE_STATE);
  @Input() set linkableArTrans(linkableArTrans: Loadable<AbstractArTran[]>) {
    emitValueIfValid(linkableArTrans, this.linkableArTrans$);
  }

  // searchValue$ = new BehaviorSubject<string>('');
  // @Input() set searchValue(searchValue: string) {
  //   emitValueIfValid(searchValue, this.searchValue$);
  // }

  @Output() pendingAllocationsChange = new EventEmitter<Record<string, number>>();

  //#endregion

  //#region Meta/Misc/Constants

  readonly CheckboxState = CheckboxState;
  readonly LoadableStatus = LoadableStatus;
  readonly AllocationStatus = AllocationStatus;
  readonly canShowValidationErrors = canShowValidationErrors;
  isInCreateFlow = false;
  private readonly _unsubscribe$ = new Subject<void>();

  focusedIndex: number;
  allTransactionIds: number[];
  @ViewChildren('allocationsContainer') allocationsContainer: QueryList<ElementRef<HTMLDivElement>>;
  resetFocusedIndexOnFocusOut$ = defer(() => getFirstOfQueryList(this.allocationsContainer)).pipe(
    tap((allocationsContainer) => allocationsContainer.nativeElement.addEventListener('focusout', () => (this.focusedIndex = undefined))),
    takeUntil(this._unsubscribe$),
  );

  @ViewChildren('headerCheckbox') headerCheckbox: QueryList<MatCheckbox>;
  focusOnHeaderCheckboxWhenEnteringEditMode$ = this.isEditActive$.pipe(
    pairwise(),
    filter(([prev, curr]) => prev === false && curr),
    switchMap((_) =>
      combineLatest([
        getFirstOfQueryList(this.headerCheckbox),
        this.headerCheckboxState$.pipe(
          filter((state) => state !== CheckboxState.DISABLED),
          take(1),
        ),
      ]),
    ),
    delay(1), // ensure checkbox has exited disabled state
    tap(([headerCheckbox]) => headerCheckbox.focus()),
    takeUntil(this._unsubscribe$),
  );

  onAllocationsContainerKeyDown(event: KeyboardEvent): void {
    if (event.key === SelectionKey.SPACE) {
      event.preventDefault();
      event.stopPropagation();

      this.headerCheckboxState$
        .pipe(
          take(1),
          tap((currentState) => this.onHeaderCheckboxClicked(currentState)),
        )
        .subscribe();

      return;
    }

    // allow the parent container to handle these (e.g. focusing on discard or save button)
    if ([SelectionKey.ENTER, NavigationKey.ESCAPE].includes(event.key as SelectionKey | NavigationKey)) {
      return;
    }

    // remaining logic is for navigation keys (so no need to continue if the key isn't one of them)
    if (!navigationKeys.includes(event.key as NavigationKey)) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    if (event.key === NavigationKey.ARROW_DOWN) {
      this.focusedIndex = [undefined, this.allTransactionIds.length - 1].includes(this.focusedIndex) ? 0 : this.focusedIndex + 1;
    } else if (event.key === NavigationKey.ARROW_UP) {
      this.focusedIndex = [undefined, 0].includes(this.focusedIndex) ? this.allTransactionIds.length - 1 : this.focusedIndex - 1;
    }

    this.focusOnAllocationRow(this.focusedIndex);
  }

  onAllocationsRowKeyDown(event: KeyboardEvent, arTran: AbstractArTran): void {
    const currentIndex = this.allTransactionIds.indexOf(arTran.id);

    const isAlreadySelected = this.pendingAllocations$.value[arTran.id] != undefined;
    const allocationsTotal = getSum(Object.values(this.pendingAllocations$.value));
    const isDisabled =
      this.allocationStatuses$.value[arTran.id] === AllocationStatus.NONE && allocationsTotal === this.selectedArTran$.value.data.amount;

    if (selectionKeys.includes(event.key as SelectionKey)) {
      event.preventDefault();
      event.stopPropagation();
      if (isDisabled) {
        return;
      }

      this.onBodyCheckboxClicked(isAlreadySelected, arTran);
      return;
    }

    if (numberKeys.includes(event.key)) {
      // focus on the amount input
      event.preventDefault();
      event.stopPropagation();
      if (isDisabled) {
        return;
      }

      const matchingFormControl = this.allocationsForm.get(`${arTran.id}`);
      matchingFormControl.setValue(event.key);
      matchingFormControl.markAsDirty();

      const allocationRow = event.currentTarget as HTMLDivElement;
      const amountInput = allocationRow.querySelector('input.allocation-input') as HTMLInputElement;
      amountInput.focus();

      return;
    }

    if (!navigationKeys.includes(event.key as NavigationKey)) {
      return;
    }

    event.preventDefault();
    event.stopPropagation();

    if (event.key === NavigationKey.ESCAPE) {
      // focus on allocations container
      this.allocationsContainer.first.nativeElement.focus();
      return;
    }

    const newIndex = (() => {
      switch (event.key as NavigationKey) {
        case NavigationKey.ARROW_UP:
          return currentIndex === 0 ? this.allTransactionIds.length - 1 : currentIndex - 1;
        case NavigationKey.ARROW_DOWN:
          return currentIndex === this.allTransactionIds.length - 1 ? 0 : currentIndex + 1;
        case NavigationKey.HOME:
        case NavigationKey.ARROW_LEFT:
          return 0;
        case NavigationKey.END:
        case NavigationKey.ARROW_RIGHT:
          return this.allTransactionIds.length - 1;
      }
    })();

    this.focusedIndex = newIndex;
    this.focusOnAllocationRow(newIndex);
  }

  onTransactionCheckboxKeyDown(event: KeyboardEvent, arTran: AbstractArTran): void {
    if ([NavigationKey.ARROW_UP, NavigationKey.ARROW_DOWN].includes(event.key as NavigationKey)) {
      return;
    }

    event.stopPropagation();

    if (event.key === SelectionKey.ENTER) {
      this.onBodyCheckboxClicked(this.pendingAllocations$.value[arTran.id] != undefined, arTran);
      return;
    }

    if (event.key === NavigationKey.ESCAPE) {
      this.focusedIndex = this.allTransactionIds.indexOf(arTran.id);
      this.focusOnAllocationRow(this.focusedIndex);
    }
  }

  onTransactionAllocationInputKeyDown(event: KeyboardEvent, arTran: AbstractArTran): void {
    event.stopPropagation();

    if ([SelectionKey.ENTER, NavigationKey.ESCAPE].includes(event.key as SelectionKey | NavigationKey)) {
      this.focusedIndex = this.allTransactionIds.indexOf(arTran.id);
      this.focusOnAllocationRow(this.focusedIndex);
      return;
    }
  }

  getAllocationRows(): HTMLDivElement[] {
    return Array.from(this.allocationsContainer.first.nativeElement.children ?? []).filter((child) =>
      child?.classList?.contains('transaction-allocation-row'),
    ) as HTMLDivElement[];
  }

  focusOnAllocationRow(index: number): void {
    const allocationRows = this.getAllocationRows();
    const nextElement = allocationRows[index] as HTMLDivElement;

    this.renderer.setAttribute(nextElement, 'tabindex', '0');
    nextElement.focus();
    this.renderer.setAttribute(nextElement, 'tabindex', '-1');
  }

  //#endregion

  //#region Summary

  allocationSummary$ = defer(() =>
    combineLatest([getLoadedResults(this.selectedArTran$).pipe(ignoreNullish()), this.pendingAllocations$, this.isEditActive$]),
  ).pipe(shareReplay(1));

  allocationTotal$ = this.allocationSummary$.pipe(
    map(([selectedArTran, pendingAllocations, isEditActive]) => {
      if (!isEditActive) {
        return selectedArTran.allocated;
      }

      return getSum(Object.values(pendingAllocations));
    }),
    shareReplay(1),
  );

  allocationStatus$ = this.allocationSummary$.pipe(
    map(([selectedArTran, pendingAllocations, isEditActive]) => {
      if (!isEditActive) {
        return selectedArTran.allocationStatus;
      }

      const allocated = getSum(Object.values(pendingAllocations));
      return AbstractArTran.getArTranAllocationStatus(true, selectedArTran.amount, allocated);
    }),
    shareReplay(1),
  );

  allocationStatusIconDetails$ = this.allocationStatus$.pipe(map((allocationStatus) => ALLOCATION_STATUS_ICON_DETAILS[allocationStatus]));

  headerCheckboxState$: Observable<CheckboxState> = defer(() =>
    combineLatest([
      this.selectedArTran$,
      this.allocationStatus$,
      this.allocationStatuses$,
      this.shouldShowLoadingState$,
      this.shouldShowZeroState$.pipe(startWith(false)),
    ]),
  ).pipe(
    map(([selectedArTran, allocationStatus, allocationStatuses, shouldShowLoadingState, shouldShowZeroState]) => {
      if (!selectedArTran.data?.amount || shouldShowLoadingState || shouldShowZeroState) {
        return CheckboxState.DISABLED;
      }

      const uniqueAllocationStatuses = removeDuplicates(Object.values(allocationStatuses));
      if (allocationStatus === AllocationStatus.FULL || hashObject(uniqueAllocationStatuses) === hashObject([AllocationStatus.FULL])) {
        return CheckboxState.CHECKED;
      }

      if (hashObject(uniqueAllocationStatuses) === hashObject([AllocationStatus.NONE])) {
        return CheckboxState.UNCHECKED;
      }

      return CheckboxState.INDETERMINATE;
    }),
  );

  onHeaderCheckboxClicked(currentState: CheckboxState): void {
    if (currentState === CheckboxState.CHECKED) {
      this.allocationsForm.reset();
      return;
    }

    let autoAllocations: BasicAllocations;
    const allLinkableArTrans = this.linkedArTrans$.value.data.concat(this.linkableArTrans$.value.data).filter(Boolean);

    if (currentState === CheckboxState.UNCHECKED) {
      autoAllocations = TransactionAllocationsComponent.getAutoAllocations(this.selectedArTran$.value.data.amount, [...allLinkableArTrans]);
    } else {
      const [arTransToConsider, arTransToIgnore] = partition(allLinkableArTrans, (arTran) => !this.pendingAllocations$.value[arTran.id]);

      const pendingAllocationsTotal = getSum(Object.values(this.pendingAllocations$.value));

      const currentAmountRemaining = roundMonetaryValue(this.selectedArTran$.value.data.amount - pendingAllocationsTotal);
      autoAllocations = TransactionAllocationsComponent.getAutoAllocations(currentAmountRemaining, [...arTransToConsider]);
      const newAllocationsTotal = getSum(Object.values(autoAllocations));

      const newAmountRemaining = roundMonetaryValue(this.selectedArTran$.value.data.amount - pendingAllocationsTotal - newAllocationsTotal);
      if (newAmountRemaining > 0) {
        const remainingAutoAllocations = TransactionAllocationsComponent.getAutoAllocations(newAmountRemaining, [...arTransToIgnore]);
        autoAllocations = { ...autoAllocations, ...remainingAutoAllocations };
      }
    }

    Object.entries(autoAllocations).forEach(([arTranId, amount]) => {
      const matchingArTran = allLinkableArTrans.find(({ id }) => id === +arTranId);
      this.setTransactionFormControlValue(matchingArTran.id, amount);
    });
  }

  setTransactionFormControlValue(arTranId: number, amount: number): void {
    const matchingFormControl = this.allocationsForm.get(`${arTranId}`);
    this.formatAmountForControl(`${amount}`, matchingFormControl);
  }

  resetTransactionAmountControl(arTranId: number): void {
    const matchingFormControl = this.allocationsForm.get(`${arTranId}`);
    matchingFormControl.reset();
  }

  //#endregion

  //#region Form

  private readonly _statusData$ = combineLatest([
    this.linkedArTrans$.pipe(pluck('status')),
    this.linkableArTrans$.pipe(pluck('status')),
    this.isEditActive$,
  ]).pipe(shareReplay(1));

  shouldShowLoadingState$ = this._statusData$.pipe(
    filter(
      ([, linkableArTransStatus]) =>
        this.selectedArTran$.value.data?.clientId != undefined || linkableArTransStatus !== LoadableStatus.DEFAULT,
    ),
    map((statusData) =>
      TransactionAllocationsComponent.getShouldShowStatusState(...statusData, [LoadableStatus.DEFAULT, LoadableStatus.LOADING]),
    ),
    shareReplay(1),
  );

  shouldShowErrorState$ = this._statusData$.pipe(
    map((statusData) => TransactionAllocationsComponent.getShouldShowStatusState(...statusData, [LoadableStatus.ERROR])),
  );

  shouldShowZeroState$ = combineLatest([this.linkedArTrans$, this.linkableArTrans$, this.isEditActive$]).pipe(
    map(([linkedArTrans, linkableArTrans, isEditActive]) => {
      const statuses = [linkedArTrans.status].concat(isEditActive ? linkableArTrans.status : []);
      const coalescedStatus = LoadableHelper.coalesceStatus(statuses, false);
      if (LoadableHelper.isLoading(coalescedStatus)) {
        return false;
      }

      const allArTrans = (linkedArTrans.data ?? []).concat(isEditActive ? (linkableArTrans.data ?? []) : []);
      this.allTransactionIds = allArTrans.map(({ id }) => id);
      return allArTrans.length === 0;
    }),
    shareReplay(1),
  );

  zeroStateMessage$ = combineLatest([getLoadedResults(this.selectedArTran$), this.isEditActive$]).pipe(
    map(([selectedArTran, isEditActive]) => {
      if (this.isInCreateFlow) {
        return selectedArTran.clientId ? 'No transactions available for this client.' : 'Select a client to see allocatable transactions.';
      }

      return isEditActive ? 'No transactions available for allocation.' : 'No allocation has been made yet.';
    }),
  );

  static getShouldShowStatusState(
    linkedStatus: LoadableStatus,
    linkableStatus: LoadableStatus,
    isEditActive: boolean,
    matchingStatuses: LoadableStatus[],
  ): boolean {
    if (!isEditActive) {
      return matchingStatuses.includes(linkedStatus);
    }

    const coalescedStatus = LoadableHelper.coalesceStatus([linkedStatus, linkableStatus]);
    return matchingStatuses.includes(coalescedStatus);
  }

  existingAllocations$ = new BehaviorSubject<BasicAllocations>({});
  setExistingAllocations$ = getLoadedResults(this.selectedArTran$).pipe(
    switchMap((selectedArTran) =>
      getLoadedResults(this.linkedArTrans$).pipe(
        map((linkedArTrans) =>
          linkedArTrans.reduce((acc, linkedArTran) => {
            const existingLink = selectedArTran.getArTranLink(linkedArTran.id);
            return { ...acc, [linkedArTran.id]: existingLink?.allocated ?? 0 };
          }, {} as BasicAllocations),
        ),
        tap((existingAllocations) => this.existingAllocations$.next(existingAllocations)),
      ),
    ),
    takeUntil(this._unsubscribe$),
  );

  pendingAllocations$ = new BehaviorSubject<BasicAllocations>({});
  setPendingAllocations$ = this.isEditActive$.pipe(
    ignoreFalsy(),
    switchMap((_) => fromSync(() => this.allocationsForm)),
    switchMap((_) => getControlValueChanges<BasicAllocations>(this.allocationsForm)),
    distinctUntilObjectChanged(),
    map((pendingAllocations) =>
      Object.entries(pendingAllocations)
        .filter(([_arTranId, amount]) => amount)
        .reduce((acc, [arTranId, amount]) => ({ ...acc, [arTranId]: extractFormattedNumber(amount) }), {} as BasicAllocations),
    ),
    tap((pendingAllocations) => this.pendingAllocations$.next(pendingAllocations)),
    takeUntil(this._unsubscribe$),
  );

  emitPendingAllocations$ = this.pendingAllocations$.pipe(
    tap((pendingAllocations) => this.pendingAllocationsChange.emit(pendingAllocations)),
    takeUntil(this._unsubscribe$),
  );

  private readonly _allocationsData$ = combineLatest([
    getLoadedResults(this.linkedArTrans$),
    this.linkableArTrans$.pipe(pluck('data')),
    this.existingAllocations$,
    this.pendingAllocations$,
    this.isEditActive$,
  ]).pipe(shareReplay(1));

  amountsRemaining$ = new BehaviorSubject<BasicAllocations>({});
  setAmountsRemaining$ = this._allocationsData$.pipe(
    map(([linkedArTrans, linkableArTrans, existingAllocations, pendingAllocations, isEditActive]) => {
      const allArTrans = linkedArTrans.concat(linkableArTrans ?? []);
      if (!isEditActive || (isEmpty(pendingAllocations) && isEmpty(existingAllocations))) {
        return allArTrans.reduce((acc, arTran) => ({ ...acc, [arTran.id]: arTran.amountRemaining }), {} as BasicAllocations);
      }

      return allArTrans.reduce((acc, arTran) => {
        const existingAllocation = existingAllocations[arTran.id] ?? 0;
        const pendingAllocation = pendingAllocations[arTran.id] ?? 0;
        const allocationChange = roundMonetaryValue(pendingAllocation - existingAllocation);
        const amountRemaining = roundMonetaryValue(arTran.amountRemaining - allocationChange);

        return { ...acc, [arTran.id]: amountRemaining };
      }, {} as BasicAllocations);
    }),
    tap((amountsRemaining) => this.amountsRemaining$.next(amountsRemaining)),
    takeUntil(this._unsubscribe$),
  );

  allocationStatuses$ = new BehaviorSubject<BasicAllocationStatuses>({});
  setAllocationStatuses$: Observable<BasicAllocationStatuses> = this._allocationsData$.pipe(
    map(([linkedArTrans, linkableArTrans, existingAllocations, pendingAllocations]) => {
      const allArTrans = linkedArTrans.concat(linkableArTrans ?? []);

      return allArTrans.reduce((acc, arTran) => {
        const pendingAllocation = pendingAllocations[arTran.id] ?? 0;
        if (pendingAllocation === 0) {
          return { ...acc, [arTran.id]: AllocationStatus.NONE };
        }

        const existingAllocation = existingAllocations[arTran.id] ?? 0;
        const amountRemaining = roundMonetaryValue(arTran.amountRemaining + existingAllocation);

        const status = AbstractArTran.getArTranAllocationStatus(true, amountRemaining, pendingAllocation);
        return { ...acc, [arTran.id]: status };
      }, {} as BasicAllocationStatuses);
    }),
    tap((allocationStatuses) => this.allocationStatuses$.next(allocationStatuses)),
    takeUntil(this._unsubscribe$),
  );

  allocationsForm: UntypedFormGroup;

  manageAllocationsForm$ = this.isEditActive$.pipe(
    switchMap((isEditActive) => {
      if (!isEditActive) {
        this.allocationsForm = undefined;
        return of(undefined);
      }

      this.allocationsForm = new UntypedFormGroup({}, this.allocationTotalValidator());

      return getLoadedResults(this.linkedArTrans$).pipe(
        tap((linkedArTrans) =>
          linkedArTrans.forEach((linkedArTran) => {
            const existingLink = this.selectedArTran$.value.data.getArTranLink(linkedArTran.id);
            this.addTransactionAmountControl(linkedArTran, existingLink?.allocated);
          }),
        ),
        switchMap((_) => getLoadedResults(this.linkableArTrans$)),
        tap((linkableArTrans) => {
          // remove any out-of-date controls (can be triggered in CREATE mode by changing client selection)
          const linkedArTranIds = this.linkedArTrans$.value.data.map(({ id }) => `${id}`);
          const oldLinkableArTrans = Object.keys(this.allocationsForm.controls).filter((arTranId) => !linkedArTranIds.includes(arTranId));
          oldLinkableArTrans.forEach((arTranId) => this.allocationsForm.removeControl(arTranId));

          // add new controls
          linkableArTrans.forEach((linkableArTran) => this.addTransactionAmountControl(linkableArTran));
        }),
        // TODO: Removed the eager auto-allocate functionality since it's causing several issues when changing total amount in CREATE mode.
        /*switchMap(_ => combineLatest([
          getLoadedResults(this.linkableArTrans$).pipe(
            // remove any out-of-date controls (can be triggered in CREATE mode by changing client selection)
            tap(_ => {
              const linkedArTranIds = this.linkedArTrans$.value.data.map(({ id }) => `${id}`);
              const oldLinkableArTrans = Object.keys(this.allocationsForm.controls).filter(arTranId => !linkedArTranIds.includes(arTranId));
              oldLinkableArTrans.forEach(arTranId => this.allocationsForm.removeControl(arTranId));
            }),
          ),
          getLoadedResults(this.selectedArTran$).pipe(
            distinctUntilKeyChanged('amount'),
          ),
        ])),
        tap(([linkableArTrans, selectedArTran]) => {
          linkableArTrans.forEach(linkableArTran => this.addTransactionAmountControl(linkableArTran));

          if (this.shouldAutoAllocateOnLoad) {
            this.onHeaderCheckboxClicked(CheckboxState.INDETERMINATE);
          }
        }),*/
      );
    }),
    takeUntil(this._unsubscribe$),
  );

  manageControlsEnabledState$ = this.isEditActive$.pipe(
    ignoreFalsy(),
    switchMap((_) =>
      combineLatest([
        this.allocationStatus$.pipe(
          map((allocationStatus) => allocationStatus === AllocationStatus.FULL),
          distinctUntilChanged(),
        ),
        fromSync(() => this.allocationsForm).pipe(
          switchMap((_) => getControlValueChanges(this.allocationsForm)),
          distinctUntilChanged((prev, curr) => {
            const previousArTranIds = Object.keys(prev);
            const currentArTranIds = Object.keys(curr);
            if (previousArTranIds.length !== currentArTranIds.length) {
              return false;
            }

            // check if both arrays have the same elements, regardless of order
            return isEmpty(xor(previousArTranIds, currentArTranIds));
          }),
        ),
      ]),
    ),
    filter((_) => {
      const selectedArTran = this.selectedArTran$.value.data;
      return !!(selectedArTran.amount && selectedArTran.clientId);
    }),
    tap(([allocationIsFull]) => {
      if (this.allocationsForm == undefined) {
        return;
      }

      if (allocationIsFull || this.allocationsForm.errors?.overAllocated) {
        const emptyControls = Object.values(this.allocationsForm.controls).filter((control) => !control.value);
        emptyControls.forEach((control) => control.disable({ emitEvent: false }));
      } else {
        Object.values(this.allocationsForm.controls).forEach((control) => control.enable({ emitEvent: false }));
      }
    }),
    takeUntil(this._unsubscribe$),
  );

  /**
   * Compares if allocated amount exceeds target amount with an epislon tolerance.
   * This prevents false positives from floating point arithmetic errors.
   *
   * @param allocatedAmount - The total allocated amount
   * @param targetAmount - The target amount to compare against
   * @returns true if the allocated amount exceeds the target amount by more than one cent
   */
  static isOverAllocated(allocatedAmount: number, targetAmount: number): boolean {
    const epsilon = 0.01; // one cent tolerance
    const difference = Math.abs(allocatedAmount) - Math.abs(targetAmount);
    const roundedDifference = roundMonetaryValue(difference);
    return roundedDifference > epsilon;
  }

  allocationTotalValidator(): ValidationErrors | null {
    return (control: UntypedFormGroup): ValidationErrors | null => {
      const linksTotal = (Object.values(control.value) as string[]).reduce(
        (acc, linkAmount) => acc + extractFormattedNumber(linkAmount),
        0,
      );
      const roundedLinksTotal = roundMonetaryValue(linksTotal);
      return this.selectedArTran$.value.data != undefined &&
        TransactionAllocationsComponent.isOverAllocated(roundedLinksTotal, this.selectedArTran$.value.data.amount)
        ? { overAllocated: true }
        : null;
    };
  }

  validateAllocationTotalOnAmountChange$ = getLoadedResults(this.selectedArTran$).pipe(
    distinctUntilKeyChanged('amount'),
    filter((_) => this.allocationsForm != undefined),
    tap((_) => this.allocationsForm.updateValueAndValidity()),
    takeUntil(this._unsubscribe$),
  );

  /**
   * Only relevant during initial stages of create mode where the form above this component is incomplete.
   */
  manageControlsEnabledStateOnAmountChange$ = getLoadedResults(this.selectedArTran$).pipe(
    take(1),
    filter(({ amount }) => !amount),

    switchMap((_) =>
      combineLatest([
        getLoadedResults(this.selectedArTran$).pipe(
          map(({ amount }) => !!amount),
          distinctUntilChanged(),
        ),
        getLoadedResults(this.selectedArTran$).pipe(
          map(({ clientId }) => clientId),
          distinctUntilChanged(),
        ),
      ]),
    ),
    skip(1), // skip the first emission (which is the initial value)

    switchMap(([isAmountPopulated]) =>
      getControlValueChanges(this.allocationsForm).pipe(
        ignoreEmpty(),
        take(1),
        tap((_) => {
          const controls = Object.values(this.allocationsForm.controls);
          const action: keyof AbstractControl & ('enable' | 'disable') = isAmountPopulated ? 'enable' : 'disable';
          controls.forEach((control) => control[action]({ emitEvent: false }));
        }),
      ),
    ),
    takeUntil(this._unsubscribe$),
  );

  addTransactionAmountControl(arTran: AbstractArTran, amount?: number): void {
    const formattedAmount = amount ? this.formatMonetaryAmount(`${amount}`) : '';
    const existingLink = this.selectedArTran$.value.data.getArTranLink(arTran.id);
    const maxValidator = Validators.max(roundMonetaryValue(arTran.amountRemaining + (existingLink?.allocated ?? 0)));

    const control = new UntypedFormControl(formattedAmount, (control) =>
      maxValidator({ ...control, value: extractFormattedNumber(control.value) } as AbstractControl),
    );
    this.allocationsForm.addControl(`${arTran.id}`, control);
  }

  onAllocationInputBlurred(amount: string, control: AbstractControl): void {
    const extractedAmount = extractFormattedNumber(amount);
    if (!extractedAmount || Number.isNaN(extractedAmount)) {
      control.reset();
      return;
    }

    this.formatAmountForControl(amount, control);
  }

  formatAmountForControl(amount: string, control: AbstractControl | null): void {
    const formattedAmount = amount ? this.formatMonetaryAmount(amount) : undefined;
    control.setValue(formattedAmount);
  }

  formatMonetaryAmount(amount: string): string {
    const amountAsNumber = extractFormattedNumber(amount);
    const finalAmount = Number.isNaN(amountAsNumber) ? 0 : amountAsNumber;
    return this.currencyPipe.transform(finalAmount, undefined, '');
  }

  static getAutoAllocations(
    amountRemaining: number,
    linkableArTrans: AbstractArTran[],
    existingAllocations: BasicAllocations = {},
    autoAllocations: BasicAllocations = {},
  ): BasicAllocations {
    if (amountRemaining === 0 || linkableArTrans.length === 0) {
      return autoAllocations;
    }

    const currentArTran = linkableArTrans.shift();
    const existingAllocation = existingAllocations[currentArTran.id] ?? 0;
    const newAllocation = Math.min(amountRemaining, roundMonetaryValue(currentArTran.amountRemaining + existingAllocation));

    return TransactionAllocationsComponent.getAutoAllocations(roundMonetaryValue(amountRemaining - newAllocation), linkableArTrans, existingAllocations, {
      ...autoAllocations,
      [currentArTran.id]: newAllocation,
    });
  }

  onBodyCheckboxClicked(isCurrentlyChecked: boolean, arTran: AbstractArTran): void {
    if (isCurrentlyChecked) {
      this.resetTransactionAmountControl(arTran.id);
      return;
    }

    const pendingAmountRemaining = roundMonetaryValue(arTran.amountRemaining + (this.existingAllocations$.value[arTran.id] ?? 0));
    const pendingAllocationsTotal = getSum(Object.values(this.pendingAllocations$.value));
    const selectedArTranAmountRemaining = roundMonetaryValue(this.selectedArTran$.value.data.amount - pendingAllocationsTotal);
    const amount = Math.min(pendingAmountRemaining, selectedArTranAmountRemaining);

    this.setTransactionFormControlValue(arTran.id, amount);
  }

  //#endregion

  //#region Lifecycle Hooks

  constructor(
    private readonly renderer: Renderer2,
    private readonly currencyPipe: CurrencyPipe,
  ) {
    this.setAmountsRemaining$.subscribe();
    this.setExistingAllocations$.subscribe();
    this.setPendingAllocations$.subscribe();
    this.setAllocationStatuses$.subscribe();
    this.emitPendingAllocations$.subscribe();
    this.manageAllocationsForm$.subscribe();
    this.manageControlsEnabledState$.subscribe();
    this.validateAllocationTotalOnAmountChange$.subscribe();
    this.manageControlsEnabledStateOnAmountChange$.subscribe();
  }

  ngAfterViewInit(): void {
    this.resetFocusedIndexOnFocusOut$.subscribe();
    this.focusOnHeaderCheckboxWhenEnteringEditMode$.subscribe();
  }

  ngOnDestroy(): void {
    this._unsubscribe$.next();
    this._unsubscribe$.complete();
  }

  //#endregion

  //#region Public Methods

  isPristine(): boolean {
    const areExistingAllocationsEmpty = isEmpty(this.existingAllocations$.value);
    const arePendingAllocationsEmpty = isEmpty(this.pendingAllocations$.value);
    if (areExistingAllocationsEmpty || arePendingAllocationsEmpty) {
      return areExistingAllocationsEmpty && arePendingAllocationsEmpty; // can only be pristine if both are empty
    }

    const areLinkedTransactionsPreserved = isEmpty(
      xor(Object.keys(this.existingAllocations$.value), Object.keys(this.pendingAllocations$.value)),
    );
    if (!areLinkedTransactionsPreserved) {
      return false;
    }

    // if the linked transactions are preserved, check if the amounts are the same
    return !Object.entries(this.pendingAllocations$.value).some(
      ([arTranId, amount]) => amount !== this.existingAllocations$.value[arTranId],
    );
  }

  validate(): Observable<boolean> {
    this.allocationsForm.markAllAsTouched();
    return of(this.allocationsForm.valid);
  }

  reset(): void {
    this.allocationsForm?.reset();
    this.pendingAllocations$.next({});
    this.focusedIndex = undefined;
    // this.searchValue$.next('');
  }

  //#endregion
}
